import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Box, Flex, Button, Text, Tag, Icon, HStack } from '@chakra-ui/react';
import { FaCalendarAlt, FaMoneyBillAlt, FaUsers } from 'react-icons/fa';
import {
  CIL,
  PRIMARY_CTA_LABEL,
  DATE_RANGE,
  BASE_PRICE,
  AGE_RANGE,
} from './constants';
import {
  formatDateAbbr,
  getEventPriceRange,
  calculateVariantAvailableSeats,
} from '../../utils';
import TextPressure from '../TextPressure';

/** Verde: molti posti (≥40%). Arancione: pochi posti (0 < x < 40%). Rosso: sold out (0%). */
function getAvailabilityColor(available, capienza) {
  if (capienza == null || capienza === 0) return 'gray.400';
  if (available == null || available <= 0) return 'red.500';
  const pct = (available / capienza) * 100;
  if (pct >= 40) return 'green.500';
  return 'orange.500';
}

function getCountdown(nowMs, targetMs) {
  const diff = Math.max(0, targetMs - nowMs);
  const totalSeconds = Math.floor(diff / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return { days, hours, minutes, seconds };
}

// Il video hero è mostrato come sfondo dal layer fisso in CentroEstivoPage (quando la sezione hero è in vista)
const HeroSection = ({
  onIscriviti,
  event,
  registrationOpensAt,
  now,
  isRegistrationOpen: isRegistrationOpenProp,
  userData,
  guestFlowActive = false,
}) => {
  const isRegistrationOpen =
    isRegistrationOpenProp ??
    (registrationOpensAt == null || now == null || now >= registrationOpensAt);

  const availabilityCircles = useMemo(() => {
    const varianti = event?.Varianti?.length
      ? [...event.Varianti].sort((a, b) =>
          (a.Data_inizio || '').localeCompare(b.Data_inizio || '')
        )
      : [];
    return varianti.map((v) => {
      const available = calculateVariantAvailableSeats(v);
      const capienza = v.Capienza != null ? v.Capienza : null;
      const color = getAvailabilityColor(
        available === 999 ? null : available,
        capienza
      );
      return { key: v.documentId || v.Nome, color };
    });
  }, [event?.Varianti]);

  const dateLabel = event?.Data
    ? event.Date_end
      ? `${formatDateAbbr(event.Data, { withYear: false })} - ${formatDateAbbr(event.Date_end, { withYear: false })}`
      : formatDateAbbr(event.Data, { withYear: false })
    : DATE_RANGE;
  const priceLabel = event ? getEventPriceRange(event) : BASE_PRICE;
  const isFree = event?.is_free ?? false;
  return (
    <Box
      position="relative"
      h={{ base: '70vh', md: '85vh' }}
      minH={{ base: '400px', md: '500px' }}
      overflow="hidden"
    >
      <Flex
        position="absolute"
        top={0}
        left={0}
        w="100%"
        h="100%"
        align="center"
        justify="center"
        direction="column"
        textAlign="center"
        color="white"
        px={6}
      >
        <Box
          position="relative"
          w="100%"
          h={{ base: '140px', md: '340px', lg: '420px' }}
          flexShrink={0}
        >
          <TextPressure
            text="CE2026"
            flex={false}
            alpha={false}
            width
            weight
            italic
            textColor={CIL.yellow}
            minFontSize={40}
          />
        </Box>
        <Text
          mt={{ base: 2, md: 3 }}
          fontSize={{ base: 'xl', md: '2xl', lg: '3xl' }}
          fontWeight="bold"
          color="white"
          textShadow="0 1px 4px rgba(0,0,0,0.8)"
        >
          {isRegistrationOpen ? (
            availabilityCircles.length > 0 ? (
              <HStack
                as="span"
                spacing={{ base: 2, md: 3 }}
                justify="center"
                display="inline-flex"
                flexWrap="wrap"
                aria-label={`Disponibilità per ${availabilityCircles.length} settimane: verde molti posti, arancione pochi posti, rosso esaurito`}
              >
                {availabilityCircles.map(({ key, color }) => (
                  <Box
                    key={key}
                    as="span"
                    w={{ base: '10px', md: '14px' }}
                    h={{ base: '10px', md: '14px' }}
                    borderRadius="full"
                    bg={color}
                    borderWidth="2px"
                    borderColor="white"
                    boxShadow="0 1px 3px rgba(0,0,0,0.4)"
                    flexShrink={0}
                  />
                ))}
              </HStack>
            ) : (
              <Box as="span">Le settimane</Box>
            )
          ) : (
            (() => {
              const { days, hours, minutes, seconds } = getCountdown(
                now ?? Date.now(),
                registrationOpensAt ?? 0
              );
              return (
                <>
                  <Box as="span" display="block">
                    Le iscrizioni aprono tra:
                  </Box>
                  <Box
                    as="span"
                    display="block"
                    color={CIL.yellow}
                    mt={{ base: 1, md: 2 }}
                  >
                    {days} g {String(hours).padStart(2, '0')} h{' '}
                    {String(minutes).padStart(2, '0')} m{' '}
                    {String(seconds).padStart(2, '0')} s
                  </Box>
                </>
              );
            })()
          )}
        </Text>
        <HStack mt={4} spacing={3} flexWrap="wrap" justify="center">
          <Tag
            size="lg"
            bg={isFree ? 'green' : CIL.sky}
            color="white"
            fontWeight="600"
            fontSize="md"
            px={4}
            py={2}
          >
            {!isFree && <Icon as={FaMoneyBillAlt} mr={2} color="white" />}
            {priceLabel}
          </Tag>
          <Tag
            size="lg"
            bg={CIL.teal}
            color="white"
            fontWeight="600"
            fontSize="md"
            px={4}
            py={2}
          >
            <Icon as={FaCalendarAlt} mr={2} />
            {dateLabel}
          </Tag>
          <Tag
            size="lg"
            bg={CIL.pink}
            color="white"
            fontWeight="600"
            fontSize="md"
            px={4}
            py={2}
          >
            <Icon as={FaUsers} mr={2} />
            {AGE_RANGE}
          </Tag>
        </HStack>
        {isRegistrationOpen &&
          (userData || guestFlowActive ? (
            <Button
              mt={8}
              size="lg"
              bg={CIL.yellow}
              color={CIL.plum}
              fontWeight="bold"
              minH={{ base: '56px', md: '60px' }}
              px={{ base: 8, md: 12 }}
              fontSize={{ base: 'xl', md: '2xl' }}
              w={{ base: '100%', md: 'auto' }}
              boxShadow="0 4px 20px rgba(0,0,0,0.25)"
              _hover={{
                bg: 'white',
                color: CIL.plum,
                boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
              }}
              onClick={onIscriviti}
            >
              {PRIMARY_CTA_LABEL}
            </Button>
          ) : (
            <Button
              as={Link}
              to="/tesseramento"
              mt={8}
              size="lg"
              bg={CIL.yellow}
              color={CIL.plum}
              fontWeight="bold"
              minH={{ base: '56px', md: '60px' }}
              px={{ base: 8, md: 12 }}
              fontSize={{ base: 'xl', md: '2xl' }}
              w={{ base: '100%', md: 'auto' }}
              boxShadow="0 4px 20px rgba(0,0,0,0.25)"
              _hover={{
                bg: 'white',
                color: CIL.plum,
                boxShadow: '0 6px 24px rgba(0,0,0,0.3)',
              }}
            >
              Diventa socio
            </Button>
          ))}
      </Flex>
    </Box>
  );
};

export default HeroSection;
